import { useCcgPolicy } from './useCcgPolicy';
import { resolveSellerHubUiSelection, ccgWorkflowFailure } from './ccg-review-state';
import { isEbayCategoryPolicyFresh } from '@tcvs/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  EbaySellerHubExportPreviewResponse,
  EbaySellerHubExportSettings,
  EbaySellerHubResultResponse,
  MarketplaceListingReadinessRecord,
  MarketplaceListingReadinessStatus,
  MarketplaceListingStorageLocation,
  MarketplaceListingActivityGroup,
  MarketplaceListingActivityReturnReason,
} from '@tcvs/shared';
import { EBAY_SELLER_HUB_EXPORT_LIMIT, EBAY_US_SELLER_POLICIES } from '@tcvs/shared';
import { useAuth } from '../auth/AuthContext';
import {
  buildStorageGroups,
  CollapsibleStorageGroups,
  StorageGroupingControl,
  storageLocationBreadcrumb,
  type StorageDisplayGroup,
  type StorageGroupMode,
  type StorageHierarchySegment,
} from '../storage/StorageHierarchy';
import {
  browserStorageHierarchyStateStore,
  readStorageGroupMode,
  readStorageHierarchyExpansion,
  writeStorageGroupMode,
  writeStorageHierarchyExpansion,
} from '../storage/storage-hierarchy-state';
import { useMarketplaceApi } from './marketplace-api';
import { EbayPublishPanel } from './EbayPublishPanel';
import {
  OwnershipLabel,
  OwnershipSummary,
  useOwnershipLabels,
} from '../consignment/OwnershipLabel';

const STATUS_LABELS: Record<MarketplaceListingReadinessStatus, string> = {
  READY_TO_LIST: 'Ready for approval',
  APPROVED_FOR_EBAY: 'Approved for eBay',
};

interface ApprovalAttempt {
  ok: boolean;
  error?: string;
  message?: string;
}

interface SellerHubChunkAttempt<T> {
  ok: boolean;
  data?: T;
  error?: string;
  message?: string;
}

function sellerHubResultError(error: string | undefined, message: string | undefined): string {
  const code = message ?? error ?? 'seller_hub_result_failed';
  return code === 'seller_hub_result_already_imported'
    ? 'This exact eBay result CSV was already imported for this export.'
    : code;
}

export interface SellerHubChunkResult<T> {
  cardIds: string[];
  data: T;
}

export interface SellerHubChunkFailure {
  chunkNumber: number;
  cardIds: string[];
  reason: string;
}

export function partitionSellerHubExportCardIds(
  cardIds: readonly string[],
  limit = EBAY_SELLER_HUB_EXPORT_LIMIT,
): string[][] {
  if (!Number.isInteger(limit) || limit < 1) throw new Error('invalid_export_chunk_limit');
  const uniqueCardIds = [...new Set(cardIds)];
  const chunks: string[][] = [];
  for (let offset = 0; offset < uniqueCardIds.length; offset += limit) {
    chunks.push(uniqueCardIds.slice(offset, offset + limit));
  }
  return chunks;
}

export async function processSellerHubExportChunks<T>(
  cardIds: readonly string[],
  process: (chunkCardIds: string[]) => Promise<SellerHubChunkAttempt<T>>,
): Promise<{
  completed: SellerHubChunkResult<T>[];
  failure?: SellerHubChunkFailure;
}> {
  const chunks = partitionSellerHubExportCardIds(cardIds);
  const completed: SellerHubChunkResult<T>[] = [];
  for (const [index, chunkCardIds] of chunks.entries()) {
    const result = await process(chunkCardIds);
    if (!result.ok || result.data === undefined) {
      return {
        completed,
        failure: {
          chunkNumber: index + 1,
          cardIds: chunkCardIds,
          reason: result.message ?? result.error ?? 'seller_hub_export_chunk_failed',
        },
      };
    }
    completed.push({ cardIds: chunkCardIds, data: result.data });
  }
  return { completed };
}

export async function approveCardsForEbay(
  cardIds: string[],
  approve: (cardId: string) => Promise<ApprovalAttempt>,
): Promise<{
  approvedIds: string[];
  failures: { cardId: string; reason: string }[];
}> {
  const approvedIds: string[] = [];
  const failures: { cardId: string; reason: string }[] = [];
  for (const cardId of cardIds) {
    const result = await approve(cardId);
    if (result.ok) {
      approvedIds.push(cardId);
    } else {
      failures.push({ cardId, reason: result.message ?? result.error ?? 'approval_failed' });
    }
  }
  return { approvedIds, failures };
}

export function updateBatchSelection(
  current: readonly string[],
  eligibleIds: readonly string[],
  limit = Number.MAX_SAFE_INTEGER,
): string[] {
  const uniqueEligible = [...new Set(eligibleIds)];
  if (uniqueEligible.length === 0) return [...current];
  const eligible = new Set(uniqueEligible);
  if (uniqueEligible.every((cardId) => current.includes(cardId))) {
    return current.filter((cardId) => !eligible.has(cardId));
  }
  if (current.length >= limit && uniqueEligible.some((cardId) => current.includes(cardId))) {
    return current.filter((cardId) => !eligible.has(cardId));
  }
  const next = [...current];
  for (const cardId of uniqueEligible) {
    if (next.length >= limit) break;
    if (!next.includes(cardId)) next.push(cardId);
  }
  return next;
}

export function filterReleaseQueueItems(
  items: readonly MarketplaceListingReadinessRecord[],
  status: MarketplaceListingReadinessStatus,
  showReleasedExports: boolean,
): MarketplaceListingReadinessRecord[] {
  if (status !== 'APPROVED_FOR_EBAY' || showReleasedExports) return [...items];
  return items.filter((item) => item.exportFingerprint === undefined);
}

export interface DuplicateReleasePlan {
  groupKey: string;
  approvedCount: number;
  committedCount: number;
  readyCount: number;
  limit: number;
  availableSlots: number;
  surplusCardIds: Set<string>;
}

export function duplicateReleasePlans(
  readyItems: readonly MarketplaceListingReadinessRecord[],
  approvedItems: readonly MarketplaceListingReadinessRecord[],
): Map<string, DuplicateReleasePlan> {
  const plans = new Map<string, DuplicateReleasePlan>();
  for (const item of [...readyItems, ...approvedItems]) {
    if (item.duplicateGroupKey === undefined) continue;
    const plan = plans.get(item.duplicateGroupKey) ?? {
      groupKey: item.duplicateGroupKey,
      approvedCount: 0,
      committedCount: 0,
      readyCount: 0,
      limit: item.duplicateGroupLimit,
      availableSlots: item.duplicateGroupLimit,
      surplusCardIds: new Set<string>(),
    };
    plan.limit = Math.min(plan.limit, item.duplicateGroupLimit);
    if (item.status === 'APPROVED_FOR_EBAY') plan.approvedCount += 1;
    else plan.readyCount += 1;
    plan.committedCount = Math.max(plan.committedCount, item.duplicateGroupCommittedCount ?? 0);
    plans.set(item.duplicateGroupKey, plan);
  }
  for (const plan of plans.values()) {
    plan.committedCount = Math.max(plan.committedCount, plan.approvedCount);
    plan.availableSlots = Math.max(0, plan.limit - plan.committedCount);
    const ready = readyItems
      .filter((item) => item.duplicateGroupKey === plan.groupKey)
      .sort((left, right) => left.queuedAt.localeCompare(right.queuedAt));
    for (const item of ready.slice(plan.availableSlots)) plan.surplusCardIds.add(item.cardId);
  }
  return plans;
}

function BatchSelectionCheckbox({
  label,
  selectedCount,
  eligibleCount,
  disabled,
  onChange,
}: {
  label: string;
  selectedCount: number;
  eligibleCount: number;
  disabled: boolean;
  onChange: () => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const allSelected = eligibleCount > 0 && selectedCount === eligibleCount;
  useEffect(() => {
    if (inputRef.current !== null) {
      inputRef.current.indeterminate = selectedCount > 0 && !allSelected;
    }
  }, [allSelected, selectedCount]);
  return (
    <label className="tcvs-storage-hierarchy-batch-select">
      <input
        ref={inputRef}
        type="checkbox"
        aria-label={label}
        checked={allSelected}
        disabled={disabled}
        onChange={onChange}
      />
      <span>Select batch</span>
    </label>
  );
}

/**
 * An export older than one eBay inventory sync may be an eBay publish that never listed the card. Publish checks it
 * and refuses anything else (a CSV export awaiting its upload result, or a publish still in progress).
 */
export function ebayPublishRetryable(exportedAt: string | undefined, now = Date.now()): boolean {
  const started = Date.parse(exportedAt ?? '');
  return Number.isFinite(started) && now - started >= 35 * 60_000;
}

export function defaultSellerHubExportSettings(): EbaySellerHubExportSettings {
  return {
    category: 'AUTO',
    listingType: 'BUY_IT_NOW',
    priceSource: 'SALE_PRICE',
    bestOfferEnabled: true,
    bestOfferAutoDeclinePercent: 80,
    location: '46037',
    paymentProfileName: EBAY_US_SELLER_POLICIES.payment.name,
    paymentProfileId: EBAY_US_SELLER_POLICIES.payment.id,
    shippingProfileName: EBAY_US_SELLER_POLICIES.shipping.PWE.name,
    shippingProfileId: EBAY_US_SELLER_POLICIES.shipping.PWE.id,
    returnProfileName: EBAY_US_SELLER_POLICIES.return.name,
    returnProfileId: EBAY_US_SELLER_POLICIES.return.id,
    franchise: '',
  };
}

export function downloadSellerHubCsv(csv: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function downloadSellerHubCsvFiles(
  files: readonly { csv: string; filename: string }[],
  shouldDownload: () => boolean = () => true,
): Promise<void> {
  if (!shouldDownload()) return;
  if (files.length === 0) throw new Error('seller_hub_csv_missing');
  if (files.length === 1) {
    const file = files[0];
    if (file === undefined) throw new Error('seller_hub_csv_missing');
    downloadSellerHubCsv(file.csv, file.filename);
    return;
  }
  const { default: JSZip } = await import('jszip');
  if (!shouldDownload()) return;
  const archive = new JSZip();
  files.forEach((file) => archive.file(file.filename, file.csv));
  const blob = await archive.generateAsync({ type: 'blob' });
  if (!shouldDownload()) return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `tcvs-ebay-${files.length}-exports.zip`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function formatPrice(item: MarketplaceListingReadinessRecord): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: item.currency,
  }).format(item.amountMinor / 100);
}

function shortStorageId(entityId: string): string {
  return entityId.replace(/^tcvs:[^:]+:/, '');
}

function storageLabel(kind: 'Shelf' | 'Box' | 'Row' | 'Batch', value: string): string {
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  const kindLower = kind.toLowerCase();
  return lower === kindLower || lower.startsWith(`${kindLower} `) ? trimmed : `${kind} ${trimmed}`;
}

export function marketplaceReadinessStoragePath(
  location: MarketplaceListingStorageLocation | undefined,
  fallbackBatchKey = 'unavailable',
): StorageHierarchySegment[] {
  if (location === undefined || location.status === 'UNAVAILABLE') {
    return [
      { level: 'shelf', key: 'unavailable', label: 'Storage location unavailable' },
      { level: 'batch', key: fallbackBatchKey, label: 'No storage batch' },
    ];
  }
  if (location.status === 'NOT_STORED') {
    return [
      { level: 'shelf', key: 'not-stored', label: 'Not in storage' },
      { level: 'batch', key: fallbackBatchKey, label: 'No storage batch' },
    ];
  }
  return [
    {
      level: 'shelf',
      key: location.shelf?.entityId ?? 'no-shelf',
      label:
        location.shelf === undefined
          ? 'No shelf'
          : storageLabel(
              'Shelf',
              location.shelf.shelfCode || shortStorageId(location.shelf.entityId),
            ),
    },
    {
      level: 'box',
      key: location.box?.entityId ?? 'no-box',
      label:
        location.box === undefined
          ? 'No box'
          : storageLabel('Box', shortStorageId(location.box.entityId)),
    },
    {
      level: 'row',
      key: location.row?.entityId ?? 'no-row',
      label:
        location.row === undefined
          ? 'No row'
          : storageLabel('Row', location.row.rowCode || shortStorageId(location.row.entityId)),
    },
    {
      level: 'batch',
      key: location.batch?.entityId ?? 'no-batch',
      label:
        location.batch === undefined
          ? 'No batch'
          : storageLabel('Batch', shortStorageId(location.batch.entityId)),
    },
  ];
}

const ACTIVITY_STATUS_LABELS: Record<MarketplaceListingActivityGroup['status'], string> = {
  PENDING: 'Awaiting result',
  PARTIALLY_RETURNED: 'Partial results',
  ACTION_REQUIRED: 'Action required',
  RETURNED: 'Returned to queue',
  RESOLVED: 'eBay import succeeded',
};

const ACTIVITY_ITEM_STATUS_LABELS: Record<
  MarketplaceListingActivityGroup['items'][number]['status'],
  string
> = {
  AWAITING_RESULT: 'Awaiting result',
  LISTED: 'eBay confirmed',
  REJECTED: 'Rejected',
  RETURNED_TO_QUEUE: 'Returned',
  RESOLVED: 'Resolved',
};

const EBAY_STATUS_LABELS = {
  ACTIVE: 'Active',
  SOLD: 'Sold',
  ENDED: 'Ended',
  MISSING: 'Missing',
  UNKNOWN: 'Unknown',
} as const;

const PICK_STATUS_LABELS = {
  READY: 'Awaiting pick',
  ALLOCATED: 'On a picklist',
  IN_PROGRESS: 'Being picked',
  PICKED: 'Picked',
  EXCEPTION: 'Pick exception',
  CANCELED: 'Pick canceled',
} as const;

function formatActivitySoldPrice(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amountMinor / 100);
}

interface ActivityResultFile {
  filename: string;
  csv: string;
}

function ListingActivityPanel(): JSX.Element {
  const marketplace = useMarketplaceApi();
  const [activities, setActivities] = useState<MarketplaceListingActivityGroup[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [reason, setReason] = useState<MarketplaceListingActivityReturnReason>('FILE_NOT_UPLOADED');
  const [filter, setFilter] = useState<'ACTIVE' | 'CLOSED' | 'ALL'>('ACTIVE');
  const [ebayStatusFilter, setEbayStatusFilter] = useState<'ALL' | keyof typeof EBAY_STATUS_LABELS>(
    'ALL',
  );
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resultFiles, setResultFiles] = useState<Record<string, ActivityResultFile>>({});
  const [resultPreviews, setResultPreviews] = useState<Record<string, EbaySellerHubResultResponse>>(
    {},
  );

  const load = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      const result = await marketplace.listMarketplaceListingActivities(cursor, 10);
      setLoading(false);
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      setActivities((current) =>
        cursor === undefined ? result.data.items : [...current, ...result.data.items],
      );
      setNextToken(result.data.nextToken);
    },
    [marketplace],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const visible = activities
    .filter((activity) => {
      if (filter === 'ALL') return true;
      const active =
        activity.status === 'PENDING' ||
        activity.status === 'PARTIALLY_RETURNED' ||
        activity.status === 'ACTION_REQUIRED';
      return filter === 'ACTIVE' ? active : !active;
    })
    .map((activity) => ({
      activity,
      visibleItems:
        ebayStatusFilter === 'ALL'
          ? activity.items
          : activity.items.filter((item) => item.ebayStatus === ebayStatusFilter),
    }))
    .filter(({ visibleItems }) => visibleItems.length > 0);

  const download = async (activity: MarketplaceListingActivityGroup) => {
    setWorking(`download:${activity.fingerprint}`);
    setError(null);
    const result = await marketplace.downloadSellerHubExport(activity.fingerprint);
    setWorking(null);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    downloadSellerHubCsv(result.data.csv, result.data.filename);
    setSuccess('Original CSV downloaded. Activity status was not changed.');
  };

  const returnItems = async (activity: MarketplaceListingActivityGroup, cardIds: string[]) => {
    if (cardIds.length === 0) return;
    if (
      !window.confirm(
        `Return ${cardIds.length} card${cardIds.length === 1 ? '' : 's'} to the release queue? Only do this after confirming no active eBay listing exists.`,
      )
    )
      return;
    setWorking(`return:${activity.fingerprint}`);
    setError(null);
    setSuccess(null);
    const result = await marketplace.returnMarketplaceListingActivityItems(
      activity.fingerprint,
      cardIds,
      reason,
    );
    setWorking(null);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    setSelected((current) => ({ ...current, [activity.fingerprint]: [] }));
    setSuccess(`${result.data.returnedCardIds.length} card(s) returned to the release queue.`);
    await load();
  };

  const chooseResultFile = async (
    activity: MarketplaceListingActivityGroup,
    file: File | undefined,
  ) => {
    if (file === undefined) return;
    setError(null);
    setSuccess(null);
    if (file.size > 2 * 1024 * 1024) {
      setError('The eBay result report must be 2 MB or smaller.');
      return;
    }
    const csv = await file.text();
    setResultFiles((current) => ({
      ...current,
      [activity.fingerprint]: { filename: file.name, csv },
    }));
    setResultPreviews((current) => {
      const next = { ...current };
      delete next[activity.fingerprint];
      return next;
    });
  };

  const previewResults = async (activity: MarketplaceListingActivityGroup) => {
    const file = resultFiles[activity.fingerprint];
    if (file === undefined) return;
    setWorking(`result-preview:${activity.fingerprint}`);
    setError(null);
    setSuccess(null);
    const result = await marketplace.previewSellerHubResults(
      activity.fingerprint,
      file.filename,
      file.csv,
    );
    setWorking(null);
    if (!result.ok) {
      setError(sellerHubResultError(result.error, result.message));
      return;
    }
    setResultPreviews((current) => ({ ...current, [activity.fingerprint]: result.data }));
  };

  const applyResults = async (activity: MarketplaceListingActivityGroup) => {
    const file = resultFiles[activity.fingerprint];
    const preview = resultPreviews[activity.fingerprint];
    if (file === undefined || preview === undefined) return;
    setWorking(`result-apply:${activity.fingerprint}`);
    setError(null);
    setSuccess(null);
    const result = await marketplace.applySellerHubResults(
      activity.fingerprint,
      file.filename,
      file.csv,
      preview.resultFingerprint,
    );
    setWorking(null);
    if (!result.ok) {
      setError(sellerHubResultError(result.error, result.message));
      return;
    }
    setResultFiles((current) => {
      const next = { ...current };
      delete next[activity.fingerprint];
      return next;
    });
    setResultPreviews((current) => {
      const next = { ...current };
      delete next[activity.fingerprint];
      return next;
    });
    setSuccess(
      `${result.data.summary.listed} listed, ${result.data.summary.rejected} rejected, and ${result.data.summary.awaitingResult} still awaiting a result.`,
    );
    await load();
  };

  return (
    <section className="tcvs-listing-activity" aria-labelledby="listing-activity-heading">
      <div className="tcvs-section-heading tcvs-listing-activity-heading">
        <div>
          <h3 id="listing-activity-heading">Listing activity</h3>
          <p>
            Track export groups, download their original files, and return only confirmed unlisted
            cards.
          </p>
        </div>
        <label>
          Export result
          <select
            aria-label="Filter by export result"
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
          >
            <option value="ACTIVE">Awaiting Listing status results</option>
            <option value="CLOSED">Received Listing status results</option>
            <option value="ALL">All</option>
          </select>
        </label>
        <label>
          eBay status
          <select
            aria-label="Filter by eBay status"
            value={ebayStatusFilter}
            onChange={(event) => {
              const next = event.target.value as typeof ebayStatusFilter;
              setEbayStatusFilter(next);
              if (next !== 'ALL') setFilter('ALL');
            }}
          >
            <option value="ALL">All</option>
            <option value="ACTIVE">Active</option>
            <option value="SOLD">Sold</option>
            <option value="ENDED">Ended</option>
            <option value="MISSING">Missing</option>
            <option value="UNKNOWN">Unknown</option>
          </select>
        </label>
      </div>
      <div className="tcvs-listing-activity-guard">
        <label>
          Return reason
          <select
            value={reason}
            onChange={(event) =>
              setReason(event.target.value as MarketplaceListingActivityReturnReason)
            }
          >
            <option value="FILE_NOT_UPLOADED">File was not uploaded</option>
            <option value="ROWS_REJECTED">Rows were rejected by eBay</option>
            <option value="NO_ACTIVE_LISTING_CONFIRMED">No active listing confirmed</option>
          </select>
        </label>
      </div>
      {error === null ? null : <p className="tcvs-error">Error: {error}</p>}
      {success === null ? null : <p className="tcvs-success-text">{success}</p>}
      {loading && activities.length === 0 ? (
        <p className="tcvs-muted">Loading listing activity…</p>
      ) : null}
      {!loading && visible.length === 0 ? (
        <p className="tcvs-muted">No listing activity matches this view.</p>
      ) : null}
      <div className="tcvs-listing-activity-groups">
        {visible.map(({ activity, visibleItems }) => {
          const isExpanded = expanded.has(activity.fingerprint);
          const returnableIds = activity.items
            .filter((item) => item.status === 'AWAITING_RESULT' || item.status === 'REJECTED')
            .map((item) => item.cardId);
          const selectedIds = selected[activity.fingerprint] ?? [];
          const resultFile = resultFiles[activity.fingerprint];
          const resultPreview = resultPreviews[activity.fingerprint];
          return (
            <article key={activity.activityId} className="tcvs-listing-activity-group">
              <div className="tcvs-listing-activity-summary">
                <button
                  type="button"
                  className="tcvs-storage-batch-toggle"
                  aria-expanded={isExpanded}
                  onClick={() =>
                    setExpanded((current) => {
                      const next = new Set(current);
                      if (next.has(activity.fingerprint)) next.delete(activity.fingerprint);
                      else next.add(activity.fingerprint);
                      return next;
                    })
                  }
                >
                  <span className="tcvs-batch-disclosure-icon" aria-hidden="true">
                    {isExpanded ? '-' : '+'}
                  </span>
                  <strong>{activity.filename}</strong>
                </button>
                <span className="tcvs-badge">{ACTIVITY_STATUS_LABELS[activity.status]}</span>
                <span>
                  {visibleItems.length === activity.items.length
                    ? `${activity.items.length} cards`
                    : `${visibleItems.length} of ${activity.items.length} cards`}
                </span>
                <span className="tcvs-muted">
                  Seller Hub CSV · {new Date(activity.createdAt).toLocaleString()}
                </span>
                <button
                  type="button"
                  className="tcvs-link-button"
                  disabled={working !== null}
                  onClick={() => void download(activity)}
                >
                  {working === `download:${activity.fingerprint}`
                    ? 'Downloading…'
                    : 'Download original CSV'}
                </button>
              </div>
              {isExpanded ? (
                <div className="tcvs-listing-activity-detail">
                  <div className="tcvs-listing-activity-actions">
                    <label className="tcvs-inline-checkbox">
                      <input
                        type="checkbox"
                        checked={
                          returnableIds.length > 0 && selectedIds.length === returnableIds.length
                        }
                        disabled={returnableIds.length === 0 || working !== null}
                        onChange={() =>
                          setSelected((current) => ({
                            ...current,
                            [activity.fingerprint]:
                              selectedIds.length === returnableIds.length ? [] : returnableIds,
                          }))
                        }
                      />
                      Select returnable cards
                    </label>
                    <button
                      type="button"
                      className="tcvs-btn-compact"
                      disabled={selectedIds.length === 0 || working !== null}
                      onClick={() => void returnItems(activity, selectedIds)}
                    >
                      Return selected ({selectedIds.length})
                    </button>
                    <button
                      type="button"
                      className="tcvs-link-button"
                      disabled={returnableIds.length === 0 || working !== null}
                      onClick={() => void returnItems(activity, returnableIds)}
                    >
                      Return all eligible
                    </button>
                  </div>
                  <div className="tcvs-listing-activity-results">
                    <label>
                      eBay upload result report
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        disabled={working !== null}
                        onChange={(event) =>
                          void chooseResultFile(activity, event.currentTarget.files?.[0])
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="tcvs-btn-compact"
                      disabled={resultFile === undefined || working !== null}
                      onClick={() => void previewResults(activity)}
                    >
                      {working === `result-preview:${activity.fingerprint}`
                        ? 'Checking results…'
                        : 'Preview results'}
                    </button>
                    {resultPreview === undefined ? null : (
                      <div className="tcvs-listing-result-preview">
                        <span>{resultPreview.summary.listed} listed</span>
                        <span>{resultPreview.summary.rejected} rejected</span>
                        <span>{resultPreview.summary.awaitingResult} awaiting</span>
                        <span>{resultPreview.summary.conflicts} conflicts</span>
                        <span>{resultPreview.summary.notInExport} not in export</span>
                        <button
                          type="button"
                          className="tcvs-btn-compact"
                          disabled={
                            resultPreview.summary.conflicts > 0 ||
                            resultPreview.summary.notInExport > 0 ||
                            working !== null
                          }
                          onClick={() => void applyResults(activity)}
                        >
                          {working === `result-apply:${activity.fingerprint}`
                            ? 'Applying results…'
                            : 'Apply results'}
                        </button>
                      </div>
                    )}
                  </div>
                  {visibleItems.map((item) => (
                    <div key={item.cardId} className="tcvs-listing-activity-item">
                      <input
                        type="checkbox"
                        aria-label={`Select ${item.marketplaceSku}`}
                        checked={selectedIds.includes(item.cardId)}
                        disabled={
                          (item.status !== 'AWAITING_RESULT' && item.status !== 'REJECTED') ||
                          working !== null
                        }
                        onChange={() =>
                          setSelected((current) => ({
                            ...current,
                            [activity.fingerprint]: selectedIds.includes(item.cardId)
                              ? selectedIds.filter((cardId) => cardId !== item.cardId)
                              : [...selectedIds, item.cardId],
                          }))
                        }
                      />
                      <div className="tcvs-listing-activity-identity">
                        <Link
                          to={`/catalog/${encodeURIComponent(item.cardId)}`}
                          className="tcvs-mono"
                        >
                          {item.marketplaceSku}
                        </Link>
                        <span>{item.title}</span>
                      </div>
                      <div className="tcvs-listing-activity-statuses">
                        <span className="tcvs-badge">
                          Export: {ACTIVITY_ITEM_STATUS_LABELS[item.status]}
                        </span>
                        {item.ebayStatus === undefined ? null : (
                          <span className="tcvs-badge" data-ebay-status={item.ebayStatus}>
                            eBay: {EBAY_STATUS_LABELS[item.ebayStatus]}
                          </span>
                        )}
                      </div>
                      <div className="tcvs-listing-activity-provider-detail">
                        {item.ebayItemId === undefined ? null : (
                          <a
                            className="tcvs-mono"
                            href={`https://www.ebay.com/itm/${item.ebayItemId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            eBay {item.ebayItemId}
                          </a>
                        )}
                        {item.soldAt === undefined ? null : (
                          <span>Sold {new Date(item.soldAt).toLocaleDateString()}</span>
                        )}
                        {item.soldAmountMinor === undefined ? null : (
                          <span>
                            Sold for{' '}
                            {formatActivitySoldPrice(
                              item.soldAmountMinor,
                              item.soldCurrency ?? 'USD',
                            )}
                          </span>
                        )}
                        {item.pickStatus === undefined ? null : (
                          <span>Pick: {PICK_STATUS_LABELS[item.pickStatus]}</span>
                        )}
                        {item.resultMessage === undefined ? null : (
                          <span
                            className={item.status === 'REJECTED' ? 'tcvs-error' : 'tcvs-muted'}
                          >
                            {item.errorCode === undefined ? '' : `${item.errorCode}: `}
                            {item.resultMessage}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
      {nextToken === undefined ? null : (
        <button
          type="button"
          className="tcvs-link-button"
          disabled={loading}
          onClick={() => void load(nextToken)}
        >
          {loading ? 'Loading…' : 'Load more activity'}
        </button>
      )}
    </section>
  );
}

export function ReadyToListPage(): JSX.Element {
  const marketplace = useMarketplaceApi();
  const { claims } = useAuth();
  const loadSequence = useRef(0);
  const reviewerId = claims?.sub;
  const currentReviewer = useRef(reviewerId);
  currentReviewer.current = reviewerId;
  const ccgHistoryVisit = useRef(0);
  const [activityOpen, setActivityOpen] = useState(false);
  const [status, setStatus] = useState<MarketplaceListingReadinessStatus>('READY_TO_LIST');
  const [surplusOnly, setSurplusOnly] = useState(false);
  const [items, setItems] = useState<MarketplaceListingReadinessRecord[]>([]);
  const [approvedPlanningItems, setApprovedPlanningItems] = useState<
    MarketplaceListingReadinessRecord[]
  >([]);
  const [showReleasedExports, setShowReleasedExports] = useState(false);
  const [selectedForApproval, setSelectedForApproval] = useState<string[]>([]);
  const [publishCardIds, setPublishCardIds] = useState<string[] | null>(null);
  const [selectedForExport, setSelectedForExport] = useState<string[]>([]);
  const [settings, setSettings] = useState<EbaySellerHubExportSettings>(
    defaultSellerHubExportSettings,
  );
  const [priceOverrideInput, setPriceOverrideInput] = useState('');
  const [previews, setPreviews] = useState<
    SellerHubChunkResult<EbaySellerHubExportPreviewResponse>[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [approvingIds, setApprovingIds] = useState<string[]>([]);
  const [downloadingFingerprint, setDownloadingFingerprint] = useState<string | null>(null);
  const [working, setWorking] = useState<'PREVIEW' | 'GENERATE' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const selection = resolveSellerHubUiSelection(items, selectedForExport, settings.category);
  const ccgSelected =
    selection.kind === 'CCG' ||
    settings.category === 'CCG_SINGLES' ||
    selectedForExport.some(
      (id) => items.find((item) => item.cardId === id)?.itemSpecificsCategory === 'CCG_SINGLES',
    );
  const ccgPolicy = useCcgPolicy(
    ccgSelected,
    JSON.stringify([reviewerId]),
    marketplace.getEbayCcgCategoryPolicy,
  );
  const ccgPolicyIdentity =
    ccgPolicy.state.status === 'AVAILABLE' ? ccgPolicy.state.reviewIdentity : '';
  const exportScope = JSON.stringify([
    reviewerId,
    selectedForExport,
    settings,
    ccgPolicyIdentity,
    items
      .filter((item) => selectedForExport.includes(item.cardId))
      .map((item) => [item.cardId, item.updatedAt, item.ccgPolicy]),
  ]);
  const latestExportScope = useRef(exportScope);
  latestExportScope.current = exportScope;
  const ccgOperationKey = JSON.stringify([
    reviewerId,
    selectedForExport,
    settings,
    ccgPolicyIdentity,
    status,
    activityOpen,
    surplusOnly,
  ]);
  const ccgOperationEpoch = useRef({ key: ccgOperationKey, marketplace, value: 0 });
  if (
    ccgOperationEpoch.current.key !== ccgOperationKey ||
    ccgOperationEpoch.current.marketplace !== marketplace
  )
    ccgOperationEpoch.current = {
      key: ccgOperationKey,
      marketplace,
      value: ccgOperationEpoch.current.value + 1,
    };
  const ccgEpoch = ccgOperationEpoch.current.value;
  const ccgBusyOwner = useRef<{ epoch: number; cleanup: () => void } | null>(null);
  const ccgPendingByActor = useRef(new Map<string, string>());
  const legacyPendingByActor = useRef(new Map<string, string[]>());
  useEffect(() => {
    const owner = ccgBusyOwner.current;
    if (owner && owner.epoch !== ccgOperationEpoch.current.value) {
      ccgBusyOwner.current = null;
      owner.cleanup();
    }
  }, [ccgEpoch]);
  useEffect(
    () => () => {
      ++ccgOperationEpoch.current.value;
      ccgBusyOwner.current = null;
    },
    [],
  );
  const beginCcgOperation = (cleanup = () => setWorking(null)) => {
    const owner = { epoch: ++ccgOperationEpoch.current.value, cleanup };
    ccgBusyOwner.current = owner;
    return {
      current: () => ccgOperationEpoch.current.value === owner.epoch,
      finish: () => {
        if (ccgBusyOwner.current === owner) {
          ccgBusyOwner.current = null;
          if (ccgOperationEpoch.current.value === owner.epoch) owner.cleanup();
        }
      },
    };
  };
  const [previewScope, setPreviewScope] = useState<string | null>(null);
  const [ccgUnknown, setCcgUnknown] = useState(false);
  const ccgUnknownFingerprint = useRef<string | null>(null);
  const [ccgKnownArchive, setCcgKnownArchive] = useState<string | null>(null);
  useEffect(() => {
    const fingerprint =
      reviewerId === undefined ? undefined : ccgPendingByActor.current.get(reviewerId);
    setCcgUnknown(fingerprint !== undefined);
    setCcgKnownArchive(null);
    ccgUnknownFingerprint.current = fingerprint ?? null;
    ++ccgHistoryVisit.current;
    setPreviewScope(null);
  }, [reviewerId, marketplace]);
  const ccgRecoveryNeeded =
    ccgPendingByActor.current.has(reviewerId ?? '') && (ccgUnknown || working === null);
  const selectedExportChunks = useMemo(
    () => (ccgSelected ? [selectedForExport] : partitionSellerHubExportCardIds(selectedForExport)),
    [selectedForExport, ccgSelected],
  );
  const exportPreviewReady =
    selection.kind !== 'BLOCKED' &&
    (!ccgSelected ||
      (!ccgRecoveryNeeded &&
        previewScope === exportScope &&
        ccgPolicy.state.status === 'AVAILABLE')) &&
    previews.length > 0 &&
    previews.length === selectedExportChunks.length &&
    previews.every(({ data }) => data.valid);
  const hierarchySurface = surplusOnly
    ? 'sales-surplus'
    : status === 'READY_TO_LIST'
      ? 'sales-ready-for-approval'
      : 'sales-approved-for-ebay';
  const [storageGroupMode, setStorageGroupMode] = useState<StorageGroupMode>(() =>
    readStorageGroupMode(
      browserStorageHierarchyStateStore(),
      'sales-ready-for-approval',
      reviewerId,
      'batch',
    ),
  );
  const [expandedStorageGroups, setExpandedStorageGroups] = useState<Set<string>>(() =>
    readStorageHierarchyExpansion(
      browserStorageHierarchyStateStore(),
      'sales-ready-for-approval',
      reviewerId,
    ),
  );

  const load = useCallback(
    async (accept?: () => boolean) => {
      if (accept?.() === false) return;
      const sequence = ++loadSequence.current;
      setLoading(true);
      setError(null);
      const loadStatus = async (
        requestedStatus: MarketplaceListingReadinessStatus,
      ): Promise<MarketplaceListingReadinessRecord[] | null> => {
        const loaded: MarketplaceListingReadinessRecord[] = [];
        let cursor: string | undefined;
        do {
          const result = await marketplace.listListingReadiness(
            requestedStatus,
            cursor,
            surplusOnly,
          );
          if (sequence !== loadSequence.current || accept?.() === false) return null;
          if (!result.ok) {
            setError(result.message ?? result.error);
            return null;
          }
          loaded.push(...result.data.items);
          cursor = result.data.nextToken;
        } while (cursor !== undefined);
        return loaded;
      };
      const loaded = await loadStatus(status);
      if (loaded === null) {
        if (accept?.() === false) return;
        setLoading(false);
        return;
      }
      const approved = status === 'READY_TO_LIST' ? await loadStatus('APPROVED_FOR_EBAY') : loaded;
      if (approved === null) {
        if (accept?.() === false) return;
        setLoading(false);
        return;
      }
      if (sequence !== loadSequence.current || accept?.() === false) return;
      setItems(loaded);
      setApprovedPlanningItems(approved);
      setLoading(false);
    },
    [marketplace, status, surplusOnly],
  );

  useEffect(() => {
    setSelectedForApproval([]);
    setSelectedForExport([]);
    setPreviews([]);
    setSuccess(null);
    void load();
  }, [load, surplusOnly]);

  useEffect(() => {
    setStorageGroupMode(
      readStorageGroupMode(
        browserStorageHierarchyStateStore(),
        hierarchySurface,
        reviewerId,
        'batch',
      ),
    );
    setExpandedStorageGroups(
      readStorageHierarchyExpansion(
        browserStorageHierarchyStateStore(),
        hierarchySurface,
        reviewerId,
      ),
    );
  }, [hierarchySurface, reviewerId]);

  const approvalBusy = approvingIds.length > 0;
  const duplicatePlans = useMemo(
    () =>
      duplicateReleasePlans(
        status === 'READY_TO_LIST' ? items : [],
        status === 'READY_TO_LIST' ? approvedPlanningItems : items,
      ),
    [approvedPlanningItems, items, status],
  );
  const surplusCardIds = useMemo(
    () => new Set([...duplicatePlans.values()].flatMap((plan) => [...plan.surplusCardIds])),
    [duplicatePlans],
  );
  const visibleItems = useMemo(() => {
    const filtered = filterReleaseQueueItems(items, status, showReleasedExports);
    return surplusOnly ? filtered.filter((item) => surplusCardIds.has(item.cardId)) : filtered;
  }, [items, showReleasedExports, status, surplusCardIds, surplusOnly]);
  const hiddenReleasedCount = items.length - visibleItems.length;
  const ownership = useOwnershipLabels(items.map((item) => item.cardId));
  const storageGroups = useMemo(() => {
    if (storageGroupMode === 'batch' || storageGroupMode === 'none') {
      return [
        {
          level: 'batch' as const,
          key: 'all',
          label: 'Batches',
          nodeKey: `${hierarchySurface}:all-batches`,
          count: visibleItems.length,
          items: visibleItems,
        },
      ];
    }
    return buildStorageGroups(
      visibleItems,
      (item) => marketplaceReadinessStoragePath(item.storageLocation, item.cardId),
      storageGroupMode,
    );
  }, [hierarchySurface, storageGroupMode, visibleItems]);
  const toggleStorageGroup = (nodeKey: string) => {
    setExpandedStorageGroups((previous) => {
      const next = new Set(previous);
      if (next.has(nodeKey)) next.delete(nodeKey);
      else next.add(nodeKey);
      writeStorageHierarchyExpansion(
        browserStorageHierarchyStateStore(),
        hierarchySurface,
        reviewerId,
        next,
      );
      return next;
    });
  };

  const approve = async (cardId: string) => {
    setApprovingIds([cardId]);
    setError(null);
    setSuccess(null);
    const result = await marketplace.approveListingReadiness(cardId);
    setApprovingIds([]);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    setSelectedForApproval((current) => current.filter((currentId) => currentId !== cardId));
    await load();
    setSuccess('Card moved to Approved for eBay.');
  };

  const approveSelected = async () => {
    const cardIds = [...selectedForApproval];
    if (cardIds.length === 0) return;
    setApprovingIds(cardIds);
    setError(null);
    setSuccess(null);
    const result = await approveCardsForEbay(cardIds, marketplace.approveListingReadiness);
    setApprovingIds([]);
    setSelectedForApproval(result.failures.map((failure) => failure.cardId));
    await load();
    if (result.failures.length > 0) {
      setError(
        `${result.approvedIds.length} approved; ${result.failures.length} failed. ${result.failures
          .map((failure) => failure.reason)
          .join(' ')}`,
      );
      return;
    }
    setSuccess(`${result.approvedIds.length} cards moved to Approved for eBay.`);
  };

  const updateSettings = <K extends keyof EbaySellerHubExportSettings>(
    key: K,
    value: EbaySellerHubExportSettings[K],
  ) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setPreviews([]);
    setSuccess(null);
  };

  const updateListingType = (listingType: EbaySellerHubExportSettings['listingType']) => {
    setSettings((current) => ({
      ...current,
      listingType,
      bestOfferEnabled: listingType === 'BUY_IT_NOW' ? current.bestOfferEnabled : false,
    }));
    setPreviews([]);
    setSuccess(null);
  };

  const updatePriceOverride = (value: string) => {
    setPriceOverrideInput(value);
    const amount = Number(value);
    updateSettings(
      'priceOverrideMinor',
      value.trim() !== '' && Number.isFinite(amount) && amount > 0
        ? Math.round(amount * 100)
        : undefined,
    );
  };

  const toggleExportSelected = (cardId: string) => {
    setSelectedForExport((current) =>
      current.includes(cardId)
        ? current.filter((currentId) => currentId !== cardId)
        : [...current, cardId],
    );
    setPreviews([]);
    setSuccess(null);
  };

  const previewExport = async () => {
    if (selection.kind === 'BLOCKED') {
      setError(selection.message);
      return;
    }
    if (selection.kind === 'CCG') {
      if (ccgUnknown || ccgPendingByActor.current.has(reviewerId ?? '')) {
        setError('Check export status before another preview or generation.');
        return;
      }
      if (
        ccgPolicy.state.status !== 'AVAILABLE' ||
        !isEbayCategoryPolicyFresh(ccgPolicy.state.policy.binding, Date.now())
      ) {
        setError('Reload the current CCG policy before preview.');
        return;
      }
      const scope = exportScope;
      const operation = beginCcgOperation();
      setWorking('PREVIEW');
      setError(null);
      setSuccess(null);
      setPreviews([]);
      try {
        const response = await marketplace.previewSellerHubExport({
          cardIds: selection.cardIds,
          settings,
        });
        if (!operation.current() || latestExportScope.current !== scope) return;
        if (!response.ok) {
          setError(ccgWorkflowFailure(response)?.message ?? response.message ?? response.error);
          return;
        }
        if (
          response.data.ccgPolicy?.reviewIdentity !== ccgPolicyIdentity ||
          response.data.limits?.ccgExportCards !== 19
        ) {
          setError('CCG policy changed. Reload and review the preview again.');
          return;
        }
        setPreviews([{ cardIds: selection.cardIds, data: response.data }]);
        setPreviewScope(scope);
      } catch {
        if (operation.current() && latestExportScope.current === scope)
          setError('CCG preview unavailable. Try again deliberately.');
      } finally {
        operation.finish();
      }
      return;
    }
    const operation = beginCcgOperation();
    setWorking('PREVIEW');
    setError(null);
    setSuccess(null);
    try {
      const result = await processSellerHubExportChunks(selectedForExport, (cardIds) =>
        operation.current()
          ? marketplace.previewSellerHubExport({ cardIds, settings })
          : Promise.resolve({ ok: false, error: 'prior_visit_ended' }),
      );
      if (!operation.current()) return;
      setPreviews(result.completed);
      if (result.failure !== undefined) {
        setError(`CSV ${result.failure.chunkNumber} preview failed: ${result.failure.reason}`);
      }
    } catch (error) {
      if (!operation.current()) return;
      setError(error instanceof Error ? error.message : 'seller_hub_preview_failed');
    } finally {
      operation.finish();
    }
  };

  const generateExport = async () => {
    if (selection.kind === 'BLOCKED') {
      setError(selection.message);
      return;
    }
    if (selection.kind === 'CCG') {
      if (
        ccgPendingByActor.current.has(reviewerId ?? '') ||
        !exportPreviewReady ||
        ccgPolicy.state.status !== 'AVAILABLE' ||
        !isEbayCategoryPolicyFresh(ccgPolicy.state.policy.binding, Date.now())
      ) {
        setError('Review a fresh CCG preview before generating.');
        return;
      }
      const scope = exportScope;
      const operation = beginCcgOperation();
      const actor = reviewerId;
      const fingerprint = previews[0]?.data.fingerprint;
      ccgUnknownFingerprint.current = fingerprint ?? null;
      if (actor !== undefined && fingerprint !== undefined)
        ccgPendingByActor.current.set(actor, fingerprint);
      setCcgKnownArchive(null);
      setWorking('GENERATE');
      setError(null);
      setSuccess(null);
      try {
        const response = await marketplace.generateSellerHubExport({
          cardIds: selection.cardIds,
          settings,
        });
        if (!operation.current() || currentReviewer.current !== actor) return;
        if (!response.ok) {
          const failure = ccgWorkflowFailure(response);
          setPreviewScope(null);
          setPreviews([]);
          if (failure?.action === 'CHECK_STATUS') {
            setCcgUnknown(true);
            await load(operation.current);
            if (!operation.current()) return;
            setError(
              'Export status is uncertain. Check status and pending exports before retrying. No CSV was downloaded.',
            );
          } else {
            if (actor !== undefined) ccgPendingByActor.current.delete(actor);
            setError(failure?.message ?? response.message ?? response.error);
          }
          return;
        }
        if (latestExportScope.current !== scope) {
          setCcgUnknown(true);
          setPreviewScope(null);
          setPreviews([]);
          setError(
            'Current listing observations changed. Check the original export status before continuing. No CSV was downloaded.',
          );
          return;
        }
        if (response.data.fingerprint !== fingerprint || response.data.csv === undefined) {
          setCcgUnknown(true);
          setPreviewScope(null);
          await load(operation.current);
          if (operation.current() && currentReviewer.current === actor)
            setError(
              'Export status changed. Check pending exports before retrying. No CSV was downloaded.',
            );
          return;
        }
        if (actor !== undefined) ccgPendingByActor.current.delete(actor);
        await downloadSellerHubCsvFiles(
          [{ csv: response.data.csv, filename: response.data.filename }],
          operation.current,
        );
        if (!operation.current()) return;
        await load(operation.current);
        if (!operation.current()) return;
        setSelectedForExport([]);
        setPreviews([]);
        setPreviewScope(null);
        setSuccess('One CCG CSV generated. No card was marked listed.');
      } catch {
        if (!operation.current() || currentReviewer.current !== actor) return;
        setCcgUnknown(true);
        setPreviewScope(null);
        setPreviews([]);
        await load(operation.current);
        if (operation.current() && currentReviewer.current === actor)
          setError(
            'Export status is uncertain. Check pending exports before retrying. No automatic retry was made.',
          );
      } finally {
        operation.finish();
      }
      return;
    }
    const operation = beginCcgOperation();
    const actor = reviewerId;
    if (actor !== undefined)
      legacyPendingByActor.current.set(
        actor,
        previews.map((preview) => preview.data.fingerprint),
      );
    setWorking('GENERATE');
    setError(null);
    setSuccess(null);
    try {
      const result = await processSellerHubExportChunks(selectedForExport, async (cardIds) => {
        if (!operation.current()) return { ok: false, error: 'prior_visit_ended' };
        const response = await marketplace.generateSellerHubExport({ cardIds, settings });
        if (!operation.current()) return { ok: false, error: 'prior_visit_ended' };
        if (!response.ok || response.data.csv !== undefined) return response;
        return { ok: false, error: 'seller_hub_csv_missing' };
      });
      if (!operation.current()) return;
      const files = result.completed.map(({ data }) => ({
        csv: data.csv as string,
        filename: data.filename,
      }));
      let downloadFailure: string | undefined;
      if (files.length > 0) {
        try {
          await downloadSellerHubCsvFiles(files, operation.current);
          if (!operation.current()) return;
        } catch {
          downloadFailure =
            'The exports were generated, but the download could not start. Use Download CSV again on the pending exports.';
        }
      }
      if (!operation.current()) return;
      const completedIds = new Set(result.completed.flatMap(({ cardIds }) => cardIds));
      await load(operation.current);
      if (!operation.current()) return;
      if (result.failure !== undefined) {
        const remainingIds = selectedForExport.filter((cardId) => !completedIds.has(cardId));
        const refreshed = await processSellerHubExportChunks(remainingIds, (cardIds) =>
          operation.current()
            ? marketplace.previewSellerHubExport({ cardIds, settings })
            : Promise.resolve({ ok: false, error: 'prior_visit_ended' }),
        );
        if (!operation.current()) return;
        setPreviews(refreshed.completed);
        setError(
          `${result.completed.length} CSV file(s) generated before CSV ${result.failure.chunkNumber} failed: ${result.failure.reason}. The remaining cards stay selected for retry.${downloadFailure === undefined ? '' : ` ${downloadFailure}`}`,
        );
      } else if (downloadFailure !== undefined) {
        setPreviews([]);
        setError(downloadFailure);
      } else if (files.length > 0) {
        setPreviews([]);
        setSuccess(
          `${files.length} CSV file${files.length === 1 ? '' : 's'} generated. No card was marked listed.`,
        );
      }
      if (!operation.current()) return;
      setSelectedForExport((current) => current.filter((cardId) => !completedIds.has(cardId)));
      if (actor !== undefined) legacyPendingByActor.current.delete(actor);
    } catch (error) {
      if (!operation.current()) return;
      setError(error instanceof Error ? error.message : 'seller_hub_export_failed');
    } finally {
      operation.finish();
    }
  };

  const checkCcgExportStatus = async () => {
    const fingerprint = ccgPendingByActor.current.get(reviewerId ?? '');
    if (!fingerprint) return;
    ccgUnknownFingerprint.current = fingerprint;
    const operation = beginCcgOperation();
    const visit = ++ccgHistoryVisit.current,
      actor = reviewerId;
    const current = () =>
      operation.current() &&
      ccgHistoryVisit.current === visit &&
      currentReviewer.current === actor &&
      ccgUnknownFingerprint.current === fingerprint;
    setError(null);
    let cursor: string | undefined;
    let found = false,
      pages = 0;
    try {
      do {
        const result = await marketplace.listMarketplaceListingActivities(cursor);
        if (!current()) return;
        ++pages;
        if (!result.ok) {
          setError('Export status could not be checked. The outcome remains uncertain.');
          return;
        }
        if (result.data.items.some((item) => item.fingerprint === fingerprint)) {
          found = true;
          break;
        }
        cursor = result.data.nextToken;
      } while (cursor !== undefined && pages < 5);
      if (!current()) return;

      if (found) {
        setCcgKnownArchive(fingerprint);
        setError(
          'The original export is recorded. Download its existing CSV; do not generate another export.',
        );
      } else
        setError(
          'The original export is not yet visible in history. Its outcome remains uncertain; do not retry or reset it. Check status again.',
        );
    } catch {
      if (current()) setError('Export status could not be checked. The outcome remains uncertain.');
    } finally {
      operation.finish();
    }
  };
  const downloadOriginalCcgArchive = async (fingerprint: string) => {
    const actor = reviewerId;
    const operation = beginCcgOperation(() => setDownloadingFingerprint(null));
    setDownloadingFingerprint(fingerprint);
    setError(null);
    try {
      const result = await marketplace.downloadSellerHubExport(fingerprint);
      if (
        !operation.current() ||
        currentReviewer.current !== actor ||
        ccgUnknownFingerprint.current !== fingerprint ||
        ccgPendingByActor.current.get(actor ?? '') !== fingerprint
      )
        return;
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      downloadSellerHubCsv(result.data.csv, result.data.filename);
      setSuccess('The original CCG CSV was downloaded. Export status was not changed.');
    } catch {
      if (operation.current() && currentReviewer.current === actor)
        setError('The original CSV could not be downloaded. Export status was not changed.');
    } finally {
      operation.finish();
    }
  };
  const downloadExportAgain = async (fingerprint: string) => {
    const operation = beginCcgOperation(() => setDownloadingFingerprint(null));
    setDownloadingFingerprint(fingerprint);
    setError(null);
    setSuccess(null);
    try {
      const result = await marketplace.downloadSellerHubExport(fingerprint);
      if (!operation.current()) return;
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      downloadSellerHubCsv(result.data.csv, result.data.filename);
      setSuccess(`${result.data.filename} downloaded again. Export status was not changed.`);
    } catch {
      if (operation.current())
        setError('The original CSV could not be downloaded. Export status was not changed.');
    } finally {
      operation.finish();
    }
  };

  const renderBatchSelection = (
    batch: StorageDisplayGroup<MarketplaceListingReadinessRecord>,
  ): JSX.Element | null => {
    if (surplusOnly) return null;
    const eligibleIds = batch.items
      .filter(
        (item) =>
          (status === 'READY_TO_LIST' && !surplusCardIds.has(item.cardId)) ||
          (status === 'APPROVED_FOR_EBAY' && item.exportFingerprint === undefined),
      )
      .map((item) => item.cardId);
    const selected = status === 'READY_TO_LIST' ? selectedForApproval : selectedForExport;
    const selectedCount = eligibleIds.filter((cardId) => selected.includes(cardId)).length;
    return (
      <BatchSelectionCheckbox
        label={`Select all ${status === 'READY_TO_LIST' ? 'cards' : 'eligible cards'} in ${batch.label} for ${status === 'READY_TO_LIST' ? 'approval' : 'export'}`}
        selectedCount={selectedCount}
        eligibleCount={eligibleIds.length}
        disabled={approvalBusy || eligibleIds.length === 0}
        onChange={() => {
          if (status === 'READY_TO_LIST') {
            setSelectedForApproval((current) => updateBatchSelection(current, eligibleIds));
          } else {
            setSelectedForExport((current) => updateBatchSelection(current, eligibleIds));
            setPreviews([]);
            setSuccess(null);
          }
        }}
      />
    );
  };

  return (
    <div className="tcvs-ready-list-workbench">
      <header className="tcvs-section-heading">
        <div>
          <h2>Queue for release</h2>
          <p>Approve reviewed listing drafts and prepare a manual eBay Seller Hub upload.</p>
        </div>
      </header>
      <nav className="tcvs-review-panel-tabs" aria-label="Queue for release statuses">
        {(['READY_TO_LIST', 'APPROVED_FOR_EBAY'] as const).map((itemStatus) => (
          <button
            key={itemStatus}
            type="button"
            aria-pressed={!activityOpen && status === itemStatus}
            onClick={() => {
              setActivityOpen(false);
              setSurplusOnly(false);
              setStatus(itemStatus);
            }}
          >
            {STATUS_LABELS[itemStatus]}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={!activityOpen && surplusOnly}
          onClick={() => {
            setActivityOpen(false);
            setStatus('READY_TO_LIST');
            setSurplusOnly(true);
          }}
        >
          Surplus
        </button>
        <button type="button" aria-pressed={activityOpen} onClick={() => setActivityOpen(true)}>
          Listing activity
        </button>
      </nav>

      {publishCardIds !== null ? (
        <EbayPublishPanel
          api={marketplace}
          cardIds={publishCardIds}
          titles={Object.fromEntries(items.map((item) => [item.cardId, item.title]))}
          images={Object.fromEntries(
            items.flatMap((item) =>
              item.imageUrl === undefined ? [] : [[item.cardId, item.imageUrl]],
            ),
          )}
          onClose={() => setPublishCardIds(null)}
          onPublished={() => {
            setSelectedForApproval([]);
            setSelectedForExport([]);
            void load();
          }}
        />
      ) : null}
      {activityOpen ? <ListingActivityPanel /> : null}
      {!activityOpen && status === 'APPROVED_FOR_EBAY' ? (
        <section className="tcvs-seller-hub-export" aria-labelledby="seller-hub-export-heading">
          <div className="tcvs-section-heading">
            <div>
              <h3 id="seller-hub-export-heading">Seller Hub CSV</h3>
              <p>
                {ccgSelected ? (
                  'Select up to 19 reviewed CCG cards for one CSV file.'
                ) : (
                  <>
                    Select any number of legacy Sport or Non-sport cards. Preview automatically
                    splits that selection into CSV files of up to {EBAY_SELLER_HUB_EXPORT_LIMIT}{' '}
                    cards.
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="tcvs-form-grid">
            <label>
              Listing type
              <select
                value={settings.listingType}
                onChange={(event) =>
                  updateListingType(
                    event.target.value as EbaySellerHubExportSettings['listingType'],
                  )
                }
              >
                <option value="BUY_IT_NOW">Buy It Now</option>
                <option value="AUCTION">Auction (7 days)</option>
              </select>
            </label>
            <label>
              Listing price
              <select
                value={settings.priceSource}
                onChange={(event) =>
                  updateSettings(
                    'priceSource',
                    event.target.value as EbaySellerHubExportSettings['priceSource'],
                  )
                }
              >
                <option value="SALE_PRICE">Sale price</option>
                <option value="CALCULATED_PRICE" disabled>
                  Calculated price (not configured)
                </option>
                <option value="PRICE_OVERRIDE">Price override</option>
              </select>
            </label>
            {settings.priceSource === 'PRICE_OVERRIDE' ? (
              <label>
                Price override
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={priceOverrideInput}
                  placeholder="0.00"
                  onChange={(event) => updatePriceOverride(event.target.value)}
                />
              </label>
            ) : null}
            <label>
              Listing category
              <select
                value={settings.category}
                onChange={(event) =>
                  updateSettings(
                    'category',
                    event.target.value as EbaySellerHubExportSettings['category'],
                  )
                }
              >
                <option value="AUTO">Automatic from reviewed category</option>
                <option value="CCG_SINGLES">Collectible card game singles (183454)</option>
                <option value="NON_SPORT_SINGLES">Non-sport singles (183050)</option>
                <option value="SPORT_SINGLES">Sport singles (261328)</option>
              </select>
            </label>
            {settings.category !== 'SPORT_SINGLES' && !ccgSelected ? (
              <label>
                Franchise override
                <input
                  value={settings.franchise ?? ''}
                  placeholder="SpongeBob SquarePants"
                  onChange={(event) => updateSettings('franchise', event.target.value)}
                />
              </label>
            ) : null}
            <label>
              Item location
              <input
                value={settings.location}
                inputMode="numeric"
                onChange={(event) => updateSettings('location', event.target.value)}
              />
            </label>
            <label>
              Shipping profile
              <select
                aria-label="Shipping profile"
                value={settings.shippingProfileId}
                onChange={(event) => {
                  const selected = Object.values(EBAY_US_SELLER_POLICIES.shipping).find(
                    (profile) => profile.id === event.target.value,
                  );
                  if (selected === undefined) return;
                  setSettings((current) => ({
                    ...current,
                    shippingProfileName: selected.name,
                    shippingProfileId: selected.id,
                  }));
                }}
              >
                {Object.values(EBAY_US_SELLER_POLICIES.shipping).map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Return profile
              <input value={settings.returnProfileName} readOnly />
            </label>
            <label>
              Payment profile
              <input value={settings.paymentProfileName} readOnly />
            </label>
          </div>
          <fieldset className="tcvs-seller-hub-offers">
            <legend>Best offers</legend>
            <label className="tcvs-inline-checkbox">
              <input
                type="checkbox"
                checked={settings.bestOfferEnabled}
                disabled={settings.listingType === 'AUCTION'}
                onChange={(event) => updateSettings('bestOfferEnabled', event.target.checked)}
              />
              Allows buyers to send offers for consideration
            </label>
            {settings.listingType === 'AUCTION' ? (
              <span className="tcvs-muted">Best offers apply to Buy It Now listings.</span>
            ) : settings.bestOfferEnabled ? (
              <div className="tcvs-form-grid">
                <label>
                  Automatically accept offers of at least
                  <span className="tcvs-percent-input">
                    <input
                      aria-label="Automatically accept offers of at least"
                      type="number"
                      min="0.01"
                      max="100"
                      step="0.01"
                      value={settings.bestOfferAutoAcceptPercent ?? ''}
                      disabled
                    />
                    <span>%</span>
                  </span>
                </label>
                <label>
                  Automatically decline offers lower than
                  <span className="tcvs-percent-input">
                    <input
                      aria-label="Automatically decline offers lower than"
                      type="number"
                      min="0.01"
                      max="100"
                      step="0.01"
                      value={settings.bestOfferAutoDeclinePercent ?? ''}
                      onChange={(event) =>
                        updateSettings(
                          'bestOfferAutoDeclinePercent',
                          event.target.value === '' ? undefined : Number(event.target.value),
                        )
                      }
                    />
                    <span>%</span>
                  </span>
                </label>
              </div>
            ) : null}
          </fieldset>
          {ccgSelected ? (
            <div aria-label="CCG export capacity">
              <p>
                Select up to 19 CCG cards for one CSV file. Game comes from the reviewed item
                details.
              </p>
              <p>Current policy: {ccgPolicy.state.status.toLowerCase()}</p>
              <button
                type="button"
                disabled={working !== null || ccgPolicy.state.status === 'LOADING'}
                onClick={ccgPolicy.reload}
              >
                Reload CCG policy
              </button>
              {ccgRecoveryNeeded ? (
                <>
                  <p>
                    Generation outcome is uncertain. Check Pending exports or download the existing
                    CSV. Do not generate again until the outcome is known.
                  </p>
                  <button type="button" onClick={() => void checkCcgExportStatus()}>
                    Check export status
                  </button>
                  {ccgKnownArchive &&
                  ccgPendingByActor.current.get(reviewerId ?? '') === ccgKnownArchive ? (
                    <button
                      type="button"
                      onClick={() => void downloadOriginalCcgArchive(ccgKnownArchive)}
                    >
                      Download original CCG CSV
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
          {selection.kind === 'BLOCKED' ? <p role="alert">{selection.message}</p> : null}
          {working === null && legacyPendingByActor.current.has(reviewerId ?? '') ? (
            <p role="status">
              An earlier export attempt awaits confirmation. Inspect Listing activity or pending
              exports. No automatic retry or download was made; already generated exports were not
              reset.
            </p>
          ) : null}
          <div className="tcvs-actions tcvs-export-actions">
            <button
              type="button"
              className="tcvs-btn-compact"
              disabled={
                selectedForExport.length === 0 || working !== null || publishCardIds !== null
              }
              onClick={() => setPublishCardIds([...selectedForExport])}
            >
              {`Publish selected to eBay (${selectedForExport.length})`}
            </button>
            <button
              type="button"
              className="tcvs-btn-export-preview"
              disabled={
                selectedForExport.length === 0 ||
                working !== null ||
                selection.kind === 'BLOCKED' ||
                (ccgSelected && (ccgRecoveryNeeded || ccgPolicy.state.status !== 'AVAILABLE'))
              }
              onClick={() => void previewExport()}
            >
              {working === 'PREVIEW'
                ? 'Checking…'
                : `Preview selected cards${
                    selectedForExport.length > 0 ? ` (${selectedForExport.length})` : ''
                  }`}
            </button>
            <button
              type="button"
              className="tcvs-btn-export-generate"
              disabled={!exportPreviewReady || working !== null}
              onClick={() => void generateExport()}
            >
              {working === 'GENERATE'
                ? 'Generating…'
                : `Generate ${Math.max(1, selectedExportChunks.length)} CSV file${
                    selectedExportChunks.length === 1 ? '' : 's'
                  }`}
            </button>
            <span className="tcvs-muted">
              Generating does not publish listings to eBay or mark cards listed.
            </span>
          </div>
          {previews.length > 0 ? (
            <div
              className={
                exportPreviewReady
                  ? 'tcvs-export-preflight tcvs-card-success'
                  : 'tcvs-export-preflight'
              }
            >
              <strong>
                {exportPreviewReady
                  ? `${previews.length} CSV file${previews.length === 1 ? '' : 's'} ready to generate`
                  : previews.some(({ data }) => !data.valid)
                    ? 'Resolve before export'
                    : 'Preview incomplete'}
              </strong>
              {previews.map(({ cardIds, data }, index) => (
                <section key={data.fingerprint} className="tcvs-export-preflight-chunk">
                  <span className="tcvs-muted">
                    CSV {index + 1} of {previews.length} · {cardIds.length} cards · Fingerprint{' '}
                    {data.fingerprint.slice(0, 12)}
                  </span>
                  {data.items.map((item) => (
                    <div key={item.cardId} className="tcvs-export-preflight-row">
                      <span>{item.shortCode ?? item.cardId}</span>
                      <div>
                        <span>{item.errors.length === 0 ? 'Ready' : item.errors.join(' ')}</span>
                        {item.conflicts?.map((conflict) => (
                          <div
                            key={`${conflict.type}:${conflict.externalListingId ?? conflict.exportFingerprint ?? 'unknown'}`}
                            className="tcvs-export-conflict"
                          >
                            <strong>
                              {conflict.type === 'PENDING_EXPORT'
                                ? 'Pending export reservation'
                                : conflict.type === 'SOLD_LISTING'
                                  ? 'Already sold on eBay'
                                  : 'Already active on eBay'}
                            </strong>
                            {conflict.externalListingId === undefined ? null : (
                              <a
                                href={`https://www.ebay.com/itm/${encodeURIComponent(conflict.externalListingId)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                eBay item {conflict.externalListingId}
                              </a>
                            )}
                            {conflict.exportFingerprint === undefined ? null : (
                              <button
                                type="button"
                                className="tcvs-link-button"
                                onClick={() => setActivityOpen(true)}
                              >
                                Export {conflict.exportFingerprint.slice(0, 12)}
                              </button>
                            )}
                            {conflict.occurredAt === undefined ? null : (
                              <span className="tcvs-muted">
                                {new Date(conflict.occurredAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {item.warnings.length > 0 ? (
                        <span className="tcvs-muted">{item.warnings.join(' ')}</span>
                      ) : null}
                    </div>
                  ))}
                </section>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {!activityOpen ? (
        <>
          <div className="tcvs-list-grouping-toolbar">
            {status === 'APPROVED_FOR_EBAY' ? (
              <label className="tcvs-inline-checkbox">
                <input
                  type="checkbox"
                  checked={showReleasedExports}
                  onChange={(event) => setShowReleasedExports(event.target.checked)}
                />
                Show released exports
                {!showReleasedExports && hiddenReleasedCount > 0
                  ? ` (${hiddenReleasedCount} hidden)`
                  : ''}
              </label>
            ) : null}
            <StorageGroupingControl
              value={storageGroupMode}
              onChange={(mode) => {
                setStorageGroupMode(mode);
                writeStorageGroupMode(
                  browserStorageHierarchyStateStore(),
                  hierarchySurface,
                  reviewerId,
                  mode,
                );
              }}
            />
          </div>

          {status === 'READY_TO_LIST' && !surplusOnly && items.length > 0 ? (
            <div className="tcvs-ready-list-bulk-actions" aria-label="Queue approval actions">
              <button
                type="button"
                className="tcvs-btn-compact"
                disabled={selectedForApproval.length === 0 || approvalBusy}
                onClick={() => void approveSelected()}
              >
                {approvalBusy
                  ? `Approving ${approvingIds.length}…`
                  : `Approve selected for eBay (${selectedForApproval.length})`}
              </button>
              <button
                type="button"
                className="tcvs-btn-compact"
                disabled={
                  selectedForApproval.length === 0 || approvalBusy || publishCardIds !== null
                }
                onClick={() => setPublishCardIds([...selectedForApproval])}
              >
                {`Publish selected to eBay (${selectedForApproval.length})`}
              </button>
            </div>
          ) : null}

          {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
          {success !== null ? <p className="tcvs-success-text">{success}</p> : null}
          {loading ? (
            <p className="tcvs-muted">Loading listing drafts…</p>
          ) : visibleItems.length === 0 ? (
            <p className="tcvs-muted">
              {surplusOnly
                ? 'No surplus cards are waiting for another sales path.'
                : `No cards in ${STATUS_LABELS[status].toLowerCase()}.`}
            </p>
          ) : (
            <div className="tcvs-ready-list-grid">
              <CollapsibleStorageGroups
                groups={storageGroups}
                expanded={expandedStorageGroups}
                onToggle={toggleStorageGroup}
                showHeaders={storageGroupMode !== 'batch' && storageGroupMode !== 'none'}
                countLabel={(group) => `${group.count} ${group.count === 1 ? 'card' : 'cards'}`}
                renderItems={(locationItems) => (
                  <div className="tcvs-storage-batch-rows">
                    {buildStorageGroups(
                      locationItems,
                      (item) => marketplaceReadinessStoragePath(item.storageLocation, item.cardId),
                      'batch',
                    ).map((batch) => {
                      const expanded = expandedStorageGroups.has(batch.nodeKey);
                      const breadcrumb = storageLocationBreadcrumb(
                        marketplaceReadinessStoragePath(
                          batch.items[0]?.storageLocation,
                          batch.items[0]?.cardId,
                        ),
                        storageGroupMode,
                      );
                      return (
                        <section key={batch.nodeKey} className="tcvs-storage-batch-row">
                          <div className="tcvs-storage-batch-heading">
                            <button
                              type="button"
                              className="tcvs-storage-batch-toggle"
                              aria-expanded={expanded}
                              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${batch.label}`}
                              title={`${expanded ? 'Collapse' : 'Expand'} ${batch.label}`}
                              onClick={() => toggleStorageGroup(batch.nodeKey)}
                            >
                              <span className="tcvs-batch-disclosure-icon" aria-hidden="true">
                                {expanded ? '-' : '+'}
                              </span>
                              <span className="tcvs-mono">{batch.label}</span>
                            </button>
                            <span className="tcvs-storage-group-count">
                              {batch.count} {batch.count === 1 ? 'card' : 'cards'}
                            </span>
                            {breadcrumb === '' ? null : (
                              <span className="tcvs-storage-location-breadcrumb">{breadcrumb}</span>
                            )}
                            {renderBatchSelection(batch)}
                          </div>
                          <OwnershipSummary
                            cardIds={batch.items.map((item) => item.cardId)}
                            labels={ownership}
                          />
                          {expanded ? (
                            <div className="tcvs-storage-batch-items">
                              {batch.items.map((item) => (
                                <article
                                  key={item.cardId}
                                  className={
                                    surplusOnly
                                      ? 'tcvs-ready-list-item tcvs-ready-list-item-readonly'
                                      : status === 'READY_TO_LIST'
                                        ? 'tcvs-ready-list-item tcvs-ready-list-item-approval'
                                        : 'tcvs-ready-list-item tcvs-ready-list-item-export'
                                  }
                                >
                                  {status === 'READY_TO_LIST' && !surplusOnly ? (
                                    <input
                                      className="tcvs-ready-list-select"
                                      type="checkbox"
                                      aria-label={`Select ${item.title} for eBay approval`}
                                      checked={selectedForApproval.includes(item.cardId)}
                                      disabled={approvalBusy || surplusCardIds.has(item.cardId)}
                                      onChange={() =>
                                        setSelectedForApproval((current) =>
                                          current.includes(item.cardId)
                                            ? current.filter(
                                                (currentId) => currentId !== item.cardId,
                                              )
                                            : [...current, item.cardId],
                                        )
                                      }
                                    />
                                  ) : status === 'APPROVED_FOR_EBAY' ? (
                                    <input
                                      className="tcvs-ready-list-select"
                                      type="checkbox"
                                      aria-label={`Select ${item.title}`}
                                      checked={selectedForExport.includes(item.cardId)}
                                      disabled={item.exportFingerprint !== undefined}
                                      onChange={() => toggleExportSelected(item.cardId)}
                                    />
                                  ) : null}
                                  {item.imageUrl === undefined ? (
                                    <span className="tcvs-ready-list-thumb tcvs-workbench-thumb-empty" />
                                  ) : (
                                    <Link to={`/catalog/${encodeURIComponent(item.cardId)}`}>
                                      <img
                                        className="tcvs-ready-list-thumb"
                                        src={item.imageUrl}
                                        alt={`${item.identity.name} card`}
                                      />
                                    </Link>
                                  )}
                                  <div className="tcvs-ready-list-copy">
                                    <div className="tcvs-ready-list-status-row">
                                      <OwnershipLabel label={ownership[item.cardId]} />
                                      <span className="tcvs-badge">
                                        {item.exportFingerprint === undefined
                                          ? STATUS_LABELS[item.status]
                                          : 'Export pending result'}
                                      </span>
                                      {item.exportFingerprint !== undefined ? (
                                        <button
                                          type="button"
                                          className="tcvs-link-button"
                                          disabled={downloadingFingerprint !== null}
                                          onClick={() =>
                                            void downloadExportAgain(
                                              item.exportFingerprint as string,
                                            )
                                          }
                                        >
                                          {downloadingFingerprint === item.exportFingerprint
                                            ? 'Downloading…'
                                            : 'Download CSV again'}
                                        </button>
                                      ) : null}
                                      {item.exportFingerprint !== undefined &&
                                      ebayPublishRetryable(item.exportedAt) ? (
                                        <button
                                          type="button"
                                          className="tcvs-link-button"
                                          disabled={publishCardIds !== null}
                                          onClick={() => setPublishCardIds([item.cardId])}
                                        >
                                          Retry on eBay
                                        </button>
                                      ) : null}
                                    </div>
                                    <strong>{item.marketplaceTitle ?? item.title}</strong>
                                    {item.duplicateGroupKey === undefined
                                      ? null
                                      : (() => {
                                          const plan = duplicatePlans.get(item.duplicateGroupKey);
                                          if (plan === undefined) return null;
                                          const surplus = plan.surplusCardIds.has(item.cardId);
                                          return (
                                            <span
                                              className={surplus ? 'tcvs-warning' : 'tcvs-muted'}
                                            >
                                              {surplus ? 'Surplus · ' : ''}
                                              Duplicate group · {plan.committedCount}/{plan.limit}{' '}
                                              marketplace slots used
                                              {plan.readyCount > 0
                                                ? ` · ${plan.readyCount} ready`
                                                : ''}
                                            </span>
                                          );
                                        })()}
                                    <span>
                                      {item.identity.name} · #{item.identity.number} ·{' '}
                                      {formatPrice(item)}
                                    </span>
                                    <span className="tcvs-muted">
                                      {item.identity.variation ?? 'Base version'}
                                    </span>
                                    <span className="tcvs-ready-list-description">
                                      {item.description?.trim() ||
                                        'A standard listing description will be added during export.'}
                                    </span>
                                    {item.exportFingerprint !== undefined ? (
                                      <button
                                        type="button"
                                        className="tcvs-link-button"
                                        disabled={
                                          working !== null || downloadingFingerprint !== null
                                        }
                                        onClick={() => setActivityOpen(true)}
                                      >
                                        Manage export activity
                                      </button>
                                    ) : null}
                                  </div>
                                  {item.status === 'READY_TO_LIST' && !surplusOnly ? (
                                    <button
                                      type="button"
                                      className="tcvs-btn-compact tcvs-ready-list-approve"
                                      disabled={approvalBusy || surplusCardIds.has(item.cardId)}
                                      onClick={() => void approve(item.cardId)}
                                    >
                                      {approvingIds.includes(item.cardId)
                                        ? 'Approving…'
                                        : 'Approve for eBay'}
                                    </button>
                                  ) : null}
                                </article>
                              ))}
                            </div>
                          ) : null}
                        </section>
                      );
                    })}
                  </div>
                )}
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
